import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteIcon from '@material-ui/icons/Delete'
import { useStyles } from './styles'
import {
    useFMOConfig,
    useCreateOrUpdateFMOConfig,
    useUploadApartmentLogo,
    useDeleteApartmentLogo,
    FMOConfigInput,
} from '../../hooks/useMoveOutManagement/useMoveOutManagement'
import { useUser } from '../../hooks'

import previewLogo from '../../assets/fmo/logo.png'
import previewFromSender from '../../assets/fmo/from-sender.png'
import previewReplyTo from '../../assets/fmo/reply-to-email.png'
import previewCustomEmail from '../../assets/fmo/custom-email-message.png'
import previewLegalStatement from '../../assets/fmo/legal-statement.png'

interface FMOConfigDialogProps {
    open: boolean
    onClose: () => void
}

interface ConfigFormState {
    from_email: string
    reply_to_email: string
    legal_statement: string
    custom_email_message: string
}

const EMPTY_CONFIG_FORM: ConfigFormState = {
    from_email: '',
    reply_to_email: '',
    legal_statement: '',
    custom_email_message: '',
}

const PreviewThumbnail: React.FC<{ src: string; onClick: () => void }> = ({
    src,
    onClick,
}) => {
    const classes = useStyles()
    return (
        <img
            src={src}
            alt="Click to preview"
            className={classes.previewThumbnail}
            onClick={onClick}
        />
    )
}

export const FMOConfigDialog: React.FC<FMOConfigDialogProps> = ({
    open,
    onClose,
}) => {
    const classes = useStyles()

    const [configForm, setConfigForm] = useState<ConfigFormState>({
        ...EMPTY_CONFIG_FORM,
    })
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const [expandedPreview, setExpandedPreview] = useState<string | null>(null)
    const logoInputRef = useRef<HTMLInputElement>(null)

    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id ?? null
    const currentLogo = workspaceUser?.active_workspace?.profile_picture ?? null

    const { fmoConfig } = useFMOConfig()
    const {
        mutate: saveConfig,
        isLoading: isConfigSaving,
    } = useCreateOrUpdateFMOConfig()
    const {
        mutate: uploadLogo,
        isLoading: isLogoUploading,
    } = useUploadApartmentLogo()
    const {
        mutate: deleteLogo,
        isLoading: isLogoDeleting,
    } = useDeleteApartmentLogo()

    useEffect(() => {
        if (open) {
            setConfigForm({
                from_email: fmoConfig?.from_email ?? '',
                reply_to_email: fmoConfig?.reply_to_email ?? '',
                legal_statement: fmoConfig?.legal_statement ?? '',
                custom_email_message: fmoConfig?.email_custom_message ?? '',
            })
            setLogoPreview(currentLogo)
        }
    }, [open, fmoConfig, currentLogo])

    const handleSave = useCallback(() => {
        const input: FMOConfigInput = {
            from_email: configForm.from_email || null,
            reply_to_email: configForm.reply_to_email || null,
            legal_statement: configForm.legal_statement || null,
            email_custom_message: configForm.custom_email_message || null,
        }
        saveConfig(input, { onSuccess: () => onClose() })
    }, [configForm, saveConfig, onClose])

    const handleLogoUpload = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file || !apartmentId) return
            const localPreview = URL.createObjectURL(file)
            setLogoPreview(localPreview)
            uploadLogo(
                { apartmentId, file },
                {
                    onSuccess: (data) => {
                        URL.revokeObjectURL(localPreview)
                        setLogoPreview(data.profile_picture)
                    },
                    onError: () => {
                        URL.revokeObjectURL(localPreview)
                        setLogoPreview(currentLogo)
                    },
                },
            )
        },
        [apartmentId, currentLogo, uploadLogo],
    )

    const handleLogoRemove = useCallback(() => {
        if (!apartmentId) return
        setLogoPreview(null)
        if (logoInputRef.current) {
            logoInputRef.current.value = ''
        }
        deleteLogo(
            { apartmentId },
            {
                onError: () => {
                    setLogoPreview(currentLogo)
                },
            },
        )
    }, [apartmentId, currentLogo, deleteLogo])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: classes.configDialogPaper }}
        >
            <DialogTitle
                disableTypography
                className={classes.configDialogTitle}
            >
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <SettingsIcon style={{ color: '#6b7280' }} />
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Statement / Email Configuration
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers className={classes.configDialogContent}>
                {/* Logo Section */}
                <Box mb={3}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box flex={1}>
                            <Typography className={classes.configSectionLabel}>
                                Property Logo
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 12 }}
                            >
                                This logo will appear on the FMO statement PDF
                                and email header.
                            </Typography>
                        </Box>
                        <PreviewThumbnail
                            src={previewLogo}
                            onClick={() => setExpandedPreview(previewLogo)}
                        />
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                        <Box className={classes.logoPreviewBox}>
                            {isLogoUploading || isLogoDeleting ? (
                                <CircularProgress size={24} />
                            ) : logoPreview ? (
                                <img
                                    src={logoPreview}
                                    alt="Property logo"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    style={{ textAlign: 'center' }}
                                >
                                    No logo uploaded
                                </Typography>
                            )}
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ gap: 8 }}
                        >
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleLogoUpload}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CloudUploadIcon />}
                                onClick={() => logoInputRef.current?.click()}
                                disabled={isLogoUploading || isLogoDeleting}
                            >
                                {logoPreview ? 'Change Logo' : 'Upload Logo'}
                            </Button>
                            {logoPreview && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleLogoRemove}
                                    disabled={isLogoUploading || isLogoDeleting}
                                    style={{
                                        color: '#ef4444',
                                        borderColor: '#fca5a5',
                                    }}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* From Name */}
                <Box mb={3}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box flex={1}>
                            <Typography className={classes.configSectionLabel}>
                                Email &quot;From&quot; Name
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 8 }}
                            >
                                The sender name recipients will see in their
                                inbox (e.g. &quot;Jenny Aucker&quot; or
                                &quot;Vista Heights Billing&quot;).
                            </Typography>
                        </Box>
                        <PreviewThumbnail
                            src={previewFromSender}
                            onClick={() =>
                                setExpandedPreview(previewFromSender)
                            }
                        />
                    </Box>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="e.g. Vista Heights Billing"
                        value={configForm.from_email}
                        onChange={(e) =>
                            setConfigForm((prev) => ({
                                ...prev,
                                from_email: e.target.value,
                            }))
                        }
                    />
                </Box>

                {/* Reply-To Email */}
                <Box mb={3}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box flex={1}>
                            <Typography className={classes.configSectionLabel}>
                                Reply-To Email Address
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 8 }}
                            >
                                When a resident replies to the FMO email, their
                                response will be sent to this address.
                            </Typography>
                        </Box>
                        <PreviewThumbnail
                            src={previewReplyTo}
                            onClick={() => setExpandedPreview(previewReplyTo)}
                        />
                    </Box>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="e.g. billing@vistaheights.com"
                        type="email"
                        value={configForm.reply_to_email}
                        onChange={(e) =>
                            setConfigForm((prev) => ({
                                ...prev,
                                reply_to_email: e.target.value,
                            }))
                        }
                    />
                </Box>

                {/* Custom Email Message */}
                <Box mb={3}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box flex={1}>
                            <Typography className={classes.configSectionLabel}>
                                Custom Email Message
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 8 }}
                            >
                                A custom message included in the body of the FMO
                                email sent to residents.
                            </Typography>
                        </Box>
                        <PreviewThumbnail
                            src={previewCustomEmail}
                            onClick={() =>
                                setExpandedPreview(previewCustomEmail)
                            }
                        />
                    </Box>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        rowsMax={6}
                        placeholder="e.g. Thank you for being a valued resident. Please review the attached statement..."
                        value={configForm.custom_email_message}
                        onChange={(e) =>
                            setConfigForm((prev) => ({
                                ...prev,
                                custom_email_message: e.target.value,
                            }))
                        }
                    />
                </Box>

                {/* Legal Statement */}
                <Box>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box flex={1}>
                            <Typography className={classes.configSectionLabel}>
                                Legal Statement
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: 8 }}
                            >
                                This text will appear at the bottom of every FMO
                                statement sent to residents.
                            </Typography>
                        </Box>
                        <PreviewThumbnail
                            src={previewLegalStatement}
                            onClick={() =>
                                setExpandedPreview(previewLegalStatement)
                            }
                        />
                    </Box>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        rowsMax={6}
                        placeholder="e.g. You have 14 days to pay the balance due..."
                        value={configForm.legal_statement}
                        onChange={(e) =>
                            setConfigForm((prev) => ({
                                ...prev,
                                legal_statement: e.target.value,
                            }))
                        }
                    />
                </Box>
            </DialogContent>

            <DialogActions className={classes.configDialogActions}>
                <Button onClick={onClose} disabled={isConfigSaving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isConfigSaving}
                    disableElevation
                    startIcon={
                        isConfigSaving ? (
                            <CircularProgress size={16} />
                        ) : undefined
                    }
                >
                    {isConfigSaving ? 'Saving...' : 'Save Configuration'}
                </Button>
            </DialogActions>

            {/* Image Preview Lightbox */}
            <Dialog
                open={!!expandedPreview}
                onClose={() => setExpandedPreview(null)}
                maxWidth="lg"
                PaperProps={{ className: classes.previewLightboxPaper }}
            >
                <IconButton
                    size="small"
                    onClick={() => setExpandedPreview(null)}
                    className={classes.previewLightboxClose}
                >
                    <CloseIcon />
                </IconButton>
                {expandedPreview && (
                    <img
                        src={expandedPreview}
                        alt="Full-size preview"
                        style={{ width: '100%', display: 'block' }}
                    />
                )}
            </Dialog>
        </Dialog>
    )
}
